import { clsx } from 'clsx'
import type { ReactNode } from 'react'
import { ACCESS_STATUSES, PLATFORMS, cleanHandleInput, displayUrl, platformUrl, type AccessStatus, type PlatformKey } from '@sonder/shared'
import { Field, Input, Select, type ReviewInfo } from './Field'
import { PlatformIcon } from './PlatformIcon'
import { StatusPill } from './StatusPill'
import { Toggle } from './Toggle'
import { ArrowUpRight, Lock } from './icons'
import { OpenLink } from './OpenLink'

type Props = {
  platform: PlatformKey
  brandName?: string
  managed: boolean
  /** When set (staff prefill), the header shows a Managed switch instead of the read-only pill. */
  onManagedChange?: (v: boolean) => void
  handle: string
  onHandleChange: (v: string) => void
  /** The client's review of a handle Sonder pre-filled. */
  handleReview?: ReviewInfo
  status?: AccessStatus
  onStatusChange?: (v: AccessStatus) => void
  /** Slot for the credential entry (login + masked secret) rendered when the platform asks for credentials. */
  credentials?: ReactNode
  mode: 'client' | 'prep'
  /** Section-document paths for the handle and status fields, so the prefill "Still empty" list can jump to them. */
  anchors?: { handle: string; status: string }
  className?: string
}

/** One platform for one brand: handle, management state, access status, and credential slot. */
export function PlatformCard({ platform, brandName, managed, onManagedChange, handle, onHandleChange, handleReview, status, onStatusChange, credentials, mode, anchors, className }: Props) {
  const p = PLATFORMS[platform]
  const format = p.handleFormat
  // Older values may carry the @ or a full URL; show just the name next to the fixed prefix.
  const shown = format.kind === 'username' ? cleanHandleInput(platform, handle) : handle
  const href = platformUrl(platform, handle)
  const error = p.handleError && handle.trim() && !href ? p.handleError : undefined
  const find = p.find && brandName && !href ? { label: p.find.label(brandName), url: p.find.url(brandName) } : null
  // An @ handle hides the address, so it is spelled out under the box; a domain prefix or pasted link
  // already shows it in the box, so it gets the same open arrow as other URL fields.
  const addressInBox = format.kind === 'link' || format.prefix !== '@'
  return (
    <section aria-label={`${p.name}${brandName ? ` for ${brandName}` : ''}`} className={clsx('grid content-start gap-4 border border-gray-200 p-5', className)}>
      {/* The status sits beside the name when there's room, and drops under it on a phone rather than covering it. */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center border border-gray-200">
          <PlatformIcon platform={platform} />
        </span>
        <h4 className="min-w-fit flex-1 text-h5 font-body font-medium leading-tight">{p.name}</h4>
        {onManagedChange ? (
          <Toggle size="sm" checked={managed} onChange={onManagedChange} label="Managed by Sonder" className="shrink-0" />
        ) : (
          <StatusPill tone={managed ? 'ink' : 'neutral'} dot={false} className="shrink-0">{managed ? 'Managed by Sonder' : 'Reference only'}</StatusPill>
        )}
      </header>

      <Field label={p.handleLabel} anchor={anchors?.handle} hint={p.handleHint} error={error} review={mode === 'client' ? handleReview : undefined}>
        <div className="grid gap-2">
          <div className="flex items-center gap-2">
            <Input
              value={shown}
              onChange={(e) => onHandleChange(cleanHandleInput(platform, e.target.value))}
              placeholder={p.handlePlaceholder}
              prefixText={format.kind === 'username' ? format.prefix : undefined}
              type={format.kind === 'link' ? 'url' : 'text'}
              inputMode={format.kind === 'link' ? 'url' : undefined}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              className="min-w-0 flex-1"
            />
            {href && addressInBox && <OpenLink value={href} />}
          </div>
          {href && !addressInBox && (
            <a href={href} target="_blank" rel="noopener noreferrer" title="Open in a new tab" className="inline-flex min-w-0 items-center gap-1.5 justify-self-start text-small text-dim hover:text-ink">
              <span className="truncate">{displayUrl(href)}</span>
              <ArrowUpRight size={12} className="shrink-0" />
            </a>
          )}
          {find && (
            <a href={find.url} target="_blank" rel="noopener noreferrer" title="Open in a new tab" className="inline-flex items-center gap-1.5 justify-self-start text-small text-dim hover:text-ink">
              {find.label} <ArrowUpRight size={12} />
            </a>
          )}
        </div>
      </Field>

      {managed && (
        <>
          <p className="text-small text-charcoal">{p.accessInstructions}</p>
          {onStatusChange && (
            <Field label="Access status" anchor={anchors?.status}>
              <Select<AccessStatus> value={status ?? 'not_started'} onValueChange={onStatusChange} options={ACCESS_STATUSES} />
            </Field>
          )}
          {p.credentials && credentials && (
            <div className="grid gap-3 border-t border-gray-200 pt-4">
              <p className="inline-flex items-center gap-2 text-[0.6875rem] uppercase tracking-eyebrow text-dim">
                <Lock /> Encrypted before it is stored
              </p>
              {credentials}
            </div>
          )}
        </>
      )}
    </section>
  )
}
